import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, feedbackAPI, plansAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { Users, Video, BarChart3, AlertCircle, ChevronRight, Send, Loader2, Zap, Bell, TrendingUp, Trophy, RotateCcw, CreditCard, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export default function CoachDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ players: 0, pendingFeedback: 0, activePlan: null });
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [sendingSmart, setSendingSmart] = useState(false);
  const [realigning, setRealigning] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [clearingPhantoms, setClearingPhantoms] = useState(false);
  const [sendingBirthYearSms, setSendingBirthYearSms] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [playersRes, feedbackRes, plansRes, flaggedRes] = await Promise.all([
          adminAPI.players(),
          feedbackAPI.list(),
          plansAPI.list(),
          adminAPI.flaggedPlayers().catch(() => ({ data: { flagged: [] } })),
        ]);
        const pending = (feedbackRes.data || []).filter(f => f.status === 'pending').length;
        const activePlan = (plansRes.data || []).find(p => p.active);
        setStats({
          players: playersRes.data?.length || 0,
          pendingFeedback: pending,
          activePlan: activePlan?.title || null,
        });
        setFlagged(flaggedRes.data?.flagged || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const cards = [
    { label: 'Players', value: stats.players, icon: Users, color: 'text-[#2563EB]', bg: 'bg-[#2563EB]/15', to: '/coach/players' },
    { label: 'Pending Reviews', value: stats.pendingFeedback, icon: Video, color: stats.pendingFeedback > 0 ? 'text-[#FF9100]' : 'text-[#10B981]', bg: stats.pendingFeedback > 0 ? 'bg-[#FF9100]/15' : 'bg-[#10B981]/15', to: '/coach/feedback' },
    { label: 'Active Plan', value: stats.activePlan || 'None', icon: BarChart3, color: 'text-[#00E5FF]', bg: 'bg-[#00E5FF]/15', to: '/coach/plans' },
  ];

  return (
    <Layout title="COACH PANEL">
      <div className="px-4 py-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-[#64748B] text-xs font-bold uppercase tracking-widest">Overview</p>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight mt-1" data-testid="coach-dashboard-title">
                COMMAND CENTER
              </h2>
            </motion.div>

            <div className="space-y-3">
              {cards.map(({ label, value, icon: Icon, color, bg, to }, i) => (
                <motion.button
                  key={label}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => navigate(to)}
                  className="w-full text-left bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center gap-4 transition-all hover:border-[#2563EB] hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#2563EB]/5"
                  data-testid={`coach-card-${label.toLowerCase().replace(' ', '-')}`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${bg}`}>
                    <Icon size={22} className={color} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <span className="font-['Barlow_Condensed'] text-xl font-black">{value}</span>
                    <p className="text-[10px] text-[#64748B] font-bold uppercase tracking-widest">{label}</p>
                  </div>
                  <ChevronRight size={18} className="text-[#CBD5E1]" />
                </motion.button>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight">QUICK ACTIONS</h3>

              {/* Analytics CTA - prominent */}
              <button
                onClick={() => navigate('/coach/analytics')}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#1d4ed8] text-white font-bold uppercase tracking-wide rounded-lg px-6 py-3.5 transition-all text-sm flex items-center justify-center gap-2 shadow-md shadow-[#2563EB]/20 hover:shadow-lg hover:shadow-[#2563EB]/30"
                data-testid="quick-action-analytics"
              >
                <TrendingUp size={16} />
                Team Analytics
                <ChevronRight size={14} />
              </button>

              {/* Leaderboard CTA */}
              <button
                onClick={() => navigate('/coach/leaderboard')}
                className="w-full bg-gradient-to-r from-[#FF9100] to-[#e67e00] text-white font-bold uppercase tracking-wide rounded-lg px-6 py-3.5 transition-all text-sm flex items-center justify-center gap-2 shadow-md shadow-[#FF9100]/20 hover:shadow-lg hover:shadow-[#FF9100]/30"
                data-testid="quick-action-leaderboard"
              >
                <Trophy size={16} />
                Player Leaderboard
                <ChevronRight size={14} />
              </button>

              {[
                { label: 'Weekly Challenges', to: '/coach/challenges' },
                { label: 'Send SMS Blast', to: '/coach/sms' },
                { label: 'Add New Drill', to: '/coach/drills' },
                { label: 'Create Training Plan', to: '/coach/plans' },
                { label: 'Landing Page Videos', to: '/coach/featured' },
              ].map(({ label, to }, i) => (
                <button
                  key={label}
                  onClick={() => navigate(to)}
                  className="w-full bg-white border-2 border-[#E2E8F0] hover:border-[#0A2242] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 shadow-sm"
                  data-testid={`quick-action-${i}`}
                >
                  {label}
                  <ChevronRight size={14} />
                </button>
              ))}

              {/* Send Weekly Recap */}
              <button
                onClick={async () => {
                  setSendingRecap(true);
                  try {
                    const res = await adminAPI.sendWeeklyRecap();
                    toast.success(res.data?.message || 'Weekly recap sent!');
                  } catch {
                    toast.error('Failed to send weekly recap');
                  }
                  setSendingRecap(false);
                }}
                disabled={sendingRecap}
                className="w-full bg-[#2563EB]/10 border-2 border-[#2563EB]/30 hover:border-[#2563EB] text-[#2563EB] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-weekly-recap-btn"
              >
                {sendingRecap ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sendingRecap ? 'Sending...' : 'Send Weekly Recap'}
              </button>

              {/* Send Smart Notifications */}
              <button
                onClick={async () => {
                  setSendingSmart(true);
                  try {
                    const res = await adminAPI.sendSmartNotifications();
                    const d = res.data;
                    const breakdown = d.breakdown || {};
                    toast.success(
                      `${d.sent} notifications sent! ` +
                      `(${breakdown.streak_protection || 0} streak, ` +
                      `${breakdown.low_friction || 0} reminder, ` +
                      `${breakdown.progress || 0} progress)`
                    );
                  } catch {
                    toast.error('Failed to send notifications');
                  }
                  setSendingSmart(false);
                }}
                disabled={sendingSmart}
                className="w-full bg-[#FF9100]/10 border-2 border-[#FF9100]/30 hover:border-[#FF9100] text-[#FF9100] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-smart-notifications-btn"
              >
                {sendingSmart ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {sendingSmart ? 'Analyzing Players...' : 'Send Smart Notifications'}
              </button>

              {/* Realign Training Start Dates (one-shot fix) */}
              <button
                onClick={async () => {
                  if (!window.confirm('Realign training start dates?\n\nThis bumps any new player whose start date was set incorrectly (e.g. signed up Sun → showing Week 2 today) up to the upcoming Monday so they sit at Week 1.\n\nOnly affects players with ZERO completed drills. Safe to run.')) return;
                  setRealigning(true);
                  try {
                    const res = await adminAPI.realignTrainingDates();
                    const d = res.data;
                    toast.success(`Realigned ${d.fixed_count} player(s) → Week 1 starts ${d.target_monday}`);
                  } catch {
                    toast.error('Realign failed');
                  }
                  setRealigning(false);
                }}
                disabled={realigning}
                className="w-full bg-white border-2 border-[#0A2242] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="realign-training-dates-btn"
              >
                {realigning ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                {realigning ? 'Realigning...' : 'Realign New Players to Week 1'}
              </button>

              {/* Clear Phantom Devices — one-shot unlock for lockouts caused by stale device IDs */}
              <button
                onClick={async () => {
                  if (!window.confirm('Clear ALL registered devices and active sessions for every player?\n\nUse this to instantly unlock anyone reporting login issues from "device limit reached". The next login on any device will succeed.\n\nPlayers currently signed in stay signed in. Coaches are unaffected.\n\nSafe to run anytime.')) return;
                  setClearingPhantoms(true);
                  try {
                    const res = await adminAPI.clearPhantomDevices();
                    toast.success(`Unlocked ${res.data.players_updated} player account(s)`);
                  } catch {
                    toast.error('Clear phantom devices failed');
                  }
                  setClearingPhantoms(false);
                }}
                disabled={clearingPhantoms}
                className="w-full bg-white border-2 border-[#0A2242] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="clear-phantom-devices-btn"
              >
                {clearingPhantoms ? <Loader2 size={14} className="animate-spin" /> : <Smartphone size={14} />}
                {clearingPhantoms ? 'Clearing...' : 'Clear Phantom Devices (Unlock Lockouts)'}
              </button>

              {/* SMS reminder to players missing birth year */}
              <button
                onClick={async () => {
                  let count = '?';
                  try {
                    const aud = await adminAPI.smsMissingBirthYearAudience();
                    count = aud.data?.valid_recipients ?? '?';
                  } catch { /* fall through */ }
                  if (!window.confirm(`Send an SMS reminder to ${count} player(s) who haven't set their birth year yet?\n\nThe text links them to their profile so they can fix it in one tap. Only opted-in players with valid phone numbers will be texted.`)) return;
                  setSendingBirthYearSms(true);
                  try {
                    const res = await adminAPI.smsMissingBirthYearSend();
                    toast.success(`SMS sent: ${res.data.sent} delivered, ${(res.data.failed || []).length} failed`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'SMS send failed');
                  }
                  setSendingBirthYearSms(false);
                }}
                disabled={sendingBirthYearSms}
                className="w-full bg-white border-2 border-[#0A2242] text-[#0A2242] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="sms-missing-birth-year-btn"
              >
                {sendingBirthYearSms ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sendingBirthYearSms ? 'Sending...' : 'Remind Players to Set Birth Year (SMS)'}
              </button>

              {/* Stripe Reconcile (catches players who paid but webhook missed) */}
              <button
                onClick={async () => {
                  if (!window.confirm('Pull active subscriptions from Stripe and auto-activate any player who paid but is still showing as inactive?\n\nSafe to run anytime — only flips "Inactive → Active" for players who actually paid. Will never deactivate anyone.')) return;
                  setReconciling(true);
                  try {
                    const res = await adminAPI.reconcileStripe();
                    const d = res.data;
                    if (d.activated?.length) {
                      toast.success(`Activated ${d.activated.length}: ${d.activated.map(a => a.email).join(', ')}`);
                    } else {
                      toast.success(`All ${d.stripe_paid_emails} paid subscribers already active — nothing to fix.`);
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Stripe reconcile failed');
                  }
                  setReconciling(false);
                }}
                disabled={reconciling}
                className="w-full bg-[#635BFF]/10 border-2 border-[#635BFF]/30 hover:border-[#635BFF] text-[#635BFF] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="reconcile-stripe-btn"
              >
                {reconciling ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                {reconciling ? 'Syncing with Stripe...' : 'Sync Paid Subscribers from Stripe'}
              </button>

              {/* Backfill Founders Badge */}
              <button
                onClick={async () => {
                  if (!window.confirm('Award the Founders Badge to every existing paid subscriber who doesn\'t have one yet?\n\nProcesses oldest signups first. Stops after 1,000 total badges. Safe to run repeatedly.')) return;
                  setBackfilling(true);
                  try {
                    const res = await adminAPI.backfillFoundersBadges();
                    const d = res.data;
                    toast.success(`Founders badges awarded: ${d.awarded.length} · ${d.total_seats_used}/1000 seats used`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Backfill failed');
                  }
                  setBackfilling(false);
                }}
                disabled={backfilling}
                className="w-full bg-[#FF9100]/10 border-2 border-[#FF9100]/30 hover:border-[#FF9100] text-[#FF9100] font-bold uppercase tracking-wide rounded-lg px-6 py-3 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="backfill-founders-btn"
              >
                {backfilling ? <Loader2 size={14} className="animate-spin" /> : <Trophy size={14} />}
                {backfilling ? 'Awarding Badges...' : 'Backfill Founders Badges'}
              </button>
            </div>

            {flagged.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl p-4 space-y-3"
                data-testid="flagged-players-digest"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} className="text-[#DC2626] flex-shrink-0" />
                  <p className="text-sm font-bold text-[#991B1B]">
                    {flagged.length} player{flagged.length > 1 ? 's' : ''} flagged for possible account sharing
                  </p>
                </div>
                <div className="space-y-1.5">
                  {flagged.slice(0, 5).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/coach/player/${p.id}`)}
                      className="w-full text-left flex items-center gap-2 p-2 bg-white rounded-lg hover:bg-[#FEF2F2] transition-colors"
                      data-testid={`flagged-player-${p.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-[#0A2242] truncate">{p.name || p.email}</p>
                        <p className="text-[10px] text-[#991B1B]">{p.reasons.join(' · ')}</p>
                      </div>
                      <ChevronRight size={14} className="text-[#CBD5E1]" />
                    </button>
                  ))}
                  {flagged.length > 5 && (
                    <p className="text-[10px] text-[#991B1B] text-center pt-1">+ {flagged.length - 5} more</p>
                  )}
                </div>
              </motion.div>
            )}

            {stats.pendingFeedback > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-[#FF9100]/10 border border-[#FF9100]/30 rounded-xl p-4 flex items-center gap-3"
              >
                <AlertCircle size={20} className="text-[#FF9100] flex-shrink-0" />
                <p className="text-sm text-[#FF9100]">
                  {stats.pendingFeedback} player video{stats.pendingFeedback > 1 ? 's' : ''} waiting for review
                </p>
              </motion.div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
